import React from 'react';
import type { SliceMode, PizzaSlice, PizzaFlavor } from './PizzaBuilderTypes';
import './PizzaBuilder.css';

interface PizzaPreviewProps {
  sliceMode: SliceMode;
  slices: PizzaSlice[];
  flavors: PizzaFlavor[];
  ingredients?: any[];
  size?: number;
}

const DEFAULT_COLOR = '#2d2d2d';

function getFlavorColor(flavors: PizzaFlavor[], flavorId: string): string {
  const flavor = flavors.find((f) => f.id === flavorId);
  return flavor ? flavor.color : DEFAULT_COLOR;
}

function getFlavorName(flavors: PizzaFlavor[], slice: PizzaSlice): string {
  if (slice.flavorName) return slice.flavorName;
  const flavor = flavors.find((f) => f.id === slice.flavorId);
  return flavor ? flavor.name : '?';
}

function getSliceColor(flavors: PizzaFlavor[], slices: PizzaSlice[], portion: string, ingredients: any[] = []): string {
  const slice = slices.find((s) => s.portion === portion);
  if (!slice) return DEFAULT_COLOR;

  if (slice.flavorId === 'build-your-own' && slice.ingredients && slice.ingredients.length > 0) {
    if (slice.ingredients.length === 1) {
      const ingredientObj = ingredients.find((ing) => ing.id === slice.ingredients?.[0]);
      return ingredientObj?.color || '#4f46e5';
    } else if (slice.ingredients.length >= 2) {
      return `url(#grad-${portion})`;
    }
  }

  return slice.flavorId ? getFlavorColor(flavors, slice.flavorId) : DEFAULT_COLOR;
}

function PizzaPreviewComponent({ sliceMode, slices, flavors, ingredients = [], size = 220 }: PizzaPreviewProps) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;
  const crustR = r + 6;

  return (
    <div 
      className={`pizza-preview-wrapper ${size < 150 ? 'pizza-preview-wrapper--mini' : ''}`}
      style={{ padding: size < 150 ? '12px' : undefined, gap: size < 150 ? '8px' : undefined }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="pizza-svg"
        style={{ width: '100%', height: 'auto', flexShrink: 0, display: 'block' }}
      >
        <defs>
          {slices.map(slice => {
            if (slice.flavorId === 'build-your-own' && slice.ingredients && slice.ingredients.length >= 2) {
              const ing1 = ingredients.find(i => i.id === slice.ingredients?.[0]);
              const ing2 = ingredients.find(i => i.id === slice.ingredients?.[1]);
              const c1 = ing1?.color || '#4f46e5';
              const c2 = ing2?.color || '#4f46e5';
              return (
                <linearGradient key={slice.portion} id={`grad-${slice.portion}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={c1} />
                  <stop offset="100%" stopColor={c2} />
                </linearGradient>
              );
            }
            return null;
          })}
        </defs>
        
        {/* Crust */}
        <circle cx={cx} cy={cy} r={crustR} fill="#8B4513" />

        {sliceMode === 'whole' && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={getSliceColor(flavors, slices, 'whole', ingredients)}
          />
        )}

        {sliceMode === 'half' && (
          <>
            {/* Left half */}
            <path
              d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx} ${cy + r} Z`}
              fill={getSliceColor(flavors, slices, 'left', ingredients)}
            />
            {/* Right half */}
            <path
              d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r} Z`}
              fill={getSliceColor(flavors, slices, 'right', ingredients)}
            />
            {/* Divider line */}
            <line
              x1={cx}
              y1={cy - r}
              x2={cx}
              y2={cy + r}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
              strokeDasharray="4 3"
            />
          </>
        )}

        {sliceMode === 'quarter' && (
          <>
            {/* Top-left */}
            <path
              d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx - r} ${cy} Z`}
              fill={getSliceColor(flavors, slices, 'top-left', ingredients)}
            />
            {/* Top-right */}
            <path
              d={`M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx + r} ${cy} Z`}
              fill={getSliceColor(flavors, slices, 'top-right', ingredients)}
            />
            {/* Bottom-right */}
            <path
              d={`M ${cx} ${cy} L ${cx + r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy + r} Z`}
              fill={getSliceColor(flavors, slices, 'bottom-right', ingredients)}
            />
            {/* Bottom-left */}
            <path
              d={`M ${cx} ${cy} L ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy + r} Z`}
              fill={getSliceColor(flavors, slices, 'bottom-left', ingredients)}
            />
            {/* Divider lines */}
            <line
              x1={cx}
              y1={cy - r}
              x2={cx}
              y2={cy + r}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
              strokeDasharray="4 3"
            />
            <line
              x1={cx - r}
              y1={cy}
              x2={cx + r}
              y2={cy}
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="2"
              strokeDasharray="4 3"
            />
          </>
        )}

        {/* Sauce dots overlay for texture */}
        {sliceMode === 'whole' && slices[0]?.flavorId && slices[0]?.flavorId !== 'build-your-own' && (
          <>
            <circle cx={cx - 20} cy={cy - 18} r={5} fill="rgba(0,0,0,0.15)" />
            <circle cx={cx + 25} cy={cy + 10} r={4} fill="rgba(0,0,0,0.15)" />
            <circle cx={cx + 5} cy={cy - 35} r={5} fill="rgba(0,0,0,0.15)" />
            <circle cx={cx - 30} cy={cy + 22} r={3} fill="rgba(0,0,0,0.15)" />
          </>
        )}

        {/* Emojis for custom ingredients */}
        {slices.map((slice) => {
          if (slice.flavorId === 'build-your-own' && slice.ingredients && slice.ingredients.length > 0) {
            const ing1 = ingredients.find(i => i.id === slice.ingredients?.[0]);
            const ing2 = ingredients.find(i => i.id === slice.ingredients?.[1]);
            
            const positions: React.ReactNode[] = [];
            
            if (slice.portion === 'whole') {
              if (ing1) {
                positions.push(<text key="1a" x={cx - 30} y={cy - 30} fontSize="24" textAnchor="middle" dominantBaseline="central">{ing1.emoji}</text>);
                positions.push(<text key="1b" x={cx + 35} y={cy + 25} fontSize="24" textAnchor="middle" dominantBaseline="central">{ing1.emoji}</text>);
              }
              if (ing2) {
                positions.push(<text key="2a" x={cx + 30} y={cy - 25} fontSize="24" textAnchor="middle" dominantBaseline="central">{ing2.emoji}</text>);
                positions.push(<text key="2b" x={cx - 25} y={cy + 35} fontSize="24" textAnchor="middle" dominantBaseline="central">{ing2.emoji}</text>);
              }
            } else if (slice.portion === 'left') {
              if (ing1) {
                positions.push(<text key="1a" x={cx - 35} y={cy - 25} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing1.emoji}</text>);
                positions.push(<text key="1b" x={cx - 25} y={cy + 35} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing1.emoji}</text>);
              }
              if (ing2) {
                positions.push(<text key="2a" x={cx - 20} y={cy - 55} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing2.emoji}</text>);
                positions.push(<text key="2b" x={cx - 55} y={cy + 10} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing2.emoji}</text>);
              }
            } else if (slice.portion === 'right') {
              if (ing1) {
                positions.push(<text key="1a" x={cx + 35} y={cy - 25} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing1.emoji}</text>);
                positions.push(<text key="1b" x={cx + 25} y={cy + 35} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing1.emoji}</text>);
              }
              if (ing2) {
                positions.push(<text key="2a" x={cx + 20} y={cy - 55} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing2.emoji}</text>);
                positions.push(<text key="2b" x={cx + 55} y={cy + 10} fontSize="20" textAnchor="middle" dominantBaseline="central">{ing2.emoji}</text>);
              }
            }

            return <g key={`ing-${slice.portion}`}>{positions}</g>;
          }
          return null;
        })}

        {/* Center dot */}
        <circle cx={cx} cy={cy} r={4} fill="rgba(255,255,255,0.15)" />
      </svg>

      {/* Legend - Hide in mini mode */}
      {size >= 150 && (
        <div className="pizza-legend">
          {sliceMode === 'whole' && slices[0]?.flavorId && (
            <span className="legend-item" style={{ background: getFlavorColor(flavors, slices[0].flavorId) }}>
              {getFlavorName(flavors, slices[0])}
            </span>
          )}
          {sliceMode === 'half' && (
            <>
              {slices.find((s) => s.portion === 'left')?.flavorId && (
                <span className="legend-item" style={{ background: getFlavorColor(flavors, slices.find((s) => s.portion === 'left')!.flavorId) }}>
                  ◀ {getFlavorName(flavors, slices.find((s) => s.portion === 'left')!)}
                </span>
              )}
              {slices.find((s) => s.portion === 'right')?.flavorId && (
                <span className="legend-item" style={{ background: getFlavorColor(flavors, slices.find((s) => s.portion === 'right')!.flavorId) }}>
                  {getFlavorName(flavors, slices.find((s) => s.portion === 'right')!)} ▶
                </span>
              )}
            </>
          )}
          {sliceMode === 'quarter' &&
            (['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const).map((p) => {
              const s = slices.find((sl) => sl.portion === p);
              if (!s?.flavorId) return null;
              return (
                <span
                  key={p}
                  className="legend-item"
                  style={{ background: getFlavorColor(flavors, s.flavorId) }}
                >
                  {getFlavorName(flavors, s)}
                </span>
              );
            })}
        </div>
      )}
    </div>
  );
}

export default React.memo(PizzaPreviewComponent);
